import {
  AlertTriangle,
  Check,
  Clock,
  FileImage,
  MessageCircle,
  Paperclip,
  ShieldAlert,
  Eye,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { DTF_PRINT_WIDTH } from "@/config/dtf";
import { whatsappLink, formatNum } from "@/lib/dtf";

const noChanges = [
  "Correção de resolução",
  "Correção de cores",
  "Remoção de fundo",
  "Ajuste de tamanho",
  "Redimensionamento",
  "Centralização",
  "Correção de margens",
  "Correção de transparência",
  "Alteração de elementos",
  "Correção de textos ou ortografia",
  "Configuração ou montagem do arquivo",
  "Identificação de erros na arte",
];

const checklist = [
  `Minha arte possui no máximo ${formatNum(DTF_PRINT_WIDTH, 0)} cm de largura.`,
  "Meu arquivo está em formato PNG.",
  "Meu arquivo possui fundo transparente.",
  "Vou enviar o arquivo como Documento pelo WhatsApp.",
  "Conferi minha arte e estou ciente de que ela será impressa exatamente como enviada.",
];

const RuleCard = ({ index, icon: Icon, title, children, testId }) => (
  <article
    data-testid={testId}
    className="group relative flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#f58244]/50 hover:shadow-[0_24px_50px_-30px_rgba(0,0,0,0.35)] sm:p-7"
  >
    <div className="flex items-center justify-between">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff1e8] text-[#f58244]">
        <Icon className="h-5 w-5" strokeWidth={2.2} />
      </span>
      <span className="num text-xs tracking-[0.3em] text-neutral-300">
        0{index}
      </span>
    </div>
    <h3 className="mt-5 text-lg font-bold leading-snug text-black">{title}</h3>
    <div className="mt-3 space-y-2 text-sm leading-relaxed text-neutral-600">
      {children}
    </div>
  </article>
);

export const ArtRules = () => (
  <section
    id="regras-arte"
    aria-labelledby="regras-arte-titulo"
    data-testid="art-rules-section"
    className="scroll-mt-28 border-y border-neutral-200 bg-[#fafaf9] py-20 sm:py-24"
  >
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-3xl">
        <Reveal>
          <p className="num text-[11px] uppercase tracking-[0.35em] text-[#f58244]">
            Antes de pedir
          </p>
        </Reveal>
        <Reveal delay={0.06}>
          <h2
            id="regras-arte-titulo"
            className="mt-3 text-3xl font-extrabold leading-tight text-black sm:text-4xl"
          >
            Regras para Envio da Arte – DTF Têxtil
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600 sm:text-base">
            Trabalhamos no modelo de gráfica rápida: imprimimos exatamente o
            arquivo que você envia. Leia as regras abaixo antes de mandar sua
            arte para garantir o melhor resultado.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.16}>
        <div
          data-testid="art-rules-width-alert"
          className="mt-10 overflow-hidden rounded-2xl bg-black p-6 text-white sm:p-8"
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-8">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#f58244] text-black">
              <AlertTriangle className="h-7 w-7" strokeWidth={2.4} />
            </span>
            <div>
              <p className="num text-[11px] uppercase tracking-[0.35em] text-[#f58244]">
                Atenção
              </p>
              <p className="mt-2 text-lg font-bold leading-snug sm:text-xl">
                A largura máxima de impressão é de{" "}
                <span className="text-[#f58244]">
                  {formatNum(DTF_PRINT_WIDTH, 0)} cm
                </span>
                . Artes maiores que esse limite poderão sair cortadas.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                A largura aceita para impressão é de exatos{" "}
                {formatNum(DTF_PRINT_WIDTH, 0)} cm. Arquivos enviados com
                largura superior poderão ser impressos com partes da arte
                cortadas devido à limitação física da impressora.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        <Reveal delay={0.04}>
          <RuleCard
            index={1}
            icon={FileImage}
            title="Formato obrigatório do arquivo"
            testId="art-rule-format"
          >
            <p>
              O arquivo deve ser enviado obrigatoriamente em{" "}
              <strong className="text-black">PNG (.png)</strong>, com{" "}
              <strong className="text-black">fundo transparente</strong>, sem
              fundo branco ou colorido — exceto quando esse fundo fizer parte
              intencionalmente da própria arte.
            </p>
            <p className="rounded-xl bg-[#fff1e8] px-4 py-3 text-[13px] text-neutral-700">
              Não trabalhamos com JPG, JPEG ou outros formatos como padrão de
              produção.
            </p>
          </RuleCard>
        </Reveal>

        <Reveal delay={0.09}>
          <RuleCard
            index={2}
            icon={Paperclip}
            title="Envio exclusivamente pelo WhatsApp"
            testId="art-rule-whatsapp"
          >
            <p>
              A arte deve ser enviada pelo WhatsApp do site usando a opção{" "}
              <strong className="text-black">Documento / Arquivo</strong>.
            </p>
            <p className="rounded-xl border-l-2 border-[#f58244] bg-[#fff1e8] px-4 py-3 text-[13px] font-semibold text-black">
              Envie sua arte pelo WhatsApp como DOCUMENTO, nunca como foto ou
              imagem.
            </p>
            <p>
              Ao enviar como foto, o aplicativo comprime o arquivo e reduz a
              qualidade da impressão.
            </p>
            <a
              href={whatsappLink(
                "Olá! Vou enviar minha arte para impressão DTF têxtil (PNG com fundo transparente, até 28 cm de largura, como documento).",
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="art-rules-whatsapp-button"
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#f58244] px-5 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#e06f32]"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
              Enviar arte pelo WhatsApp
            </a>
          </RuleCard>
        </Reveal>

        <Reveal delay={0.14}>
          <RuleCard
            index={3}
            icon={Clock}
            title="Prazo de produção"
            testId="art-rule-deadline"
          >
            <p className="text-base font-bold text-black">
              Prazo de impressão: até 24 horas úteis.
            </p>
            <p>
              O prazo de até 24 horas úteis começa a contar somente após o
              recebimento correto da arte, a confirmação do pagamento e a
              confirmação do pedido.
            </p>
          </RuleCard>
        </Reveal>

        <Reveal delay={0.04} className="md:col-span-2">
          <div
            data-testid="art-rule-responsibility"
            className="flex h-full flex-col rounded-2xl border border-neutral-900 bg-black p-6 text-white sm:p-7"
          >
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#f58244]/15 text-[#f58244]">
                <ShieldAlert className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <span className="num text-xs tracking-[0.3em] text-white/25">
                04
              </span>
            </div>
            <h3 className="mt-5 text-lg font-bold leading-snug sm:text-xl">
              Responsabilidade pelo arquivo enviado
            </h3>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-white/75">
              <p>
                O arquivo enviado pelo cliente será considerado{" "}
                <strong className="text-white">aprovado e pronto para impressão</strong>
                . A impressão será realizada exatamente conforme o arquivo
                recebido e dentro das limitações técnicas do equipamento.
              </p>
              <p className="font-semibold text-white">
                Não realizamos conferência, correção ou preparação da arte antes
                da impressão. Isso inclui, mas não se limita a:
              </p>
              <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {noChanges.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]">
                    <span className="mt-1.5 h-1 w-3 shrink-0 bg-[#f58244]" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="rounded-xl border-l-2 border-[#f58244] bg-white/5 px-4 py-3 text-[13px] text-white">
                Não enviamos aviso prévio caso o arquivo possua erros, baixa
                resolução, dimensões incorretas, fundo indesejado ou qualquer
                outro problema relacionado à arte enviada. A responsabilidade
                pela conferência e preparação correta do arquivo é do cliente
                antes do envio — o que for enviado será utilizado para produção.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.09}>
          <RuleCard
            index={5}
            icon={Eye}
            title="Fidelidade ao arquivo"
            testId="art-rule-fidelity"
          >
            <p>
              A impressão será realizada com base no arquivo digital enviado
              pelo cliente. Pequenas diferenças de tonalidade podem ocorrer
              entre a visualização em telas de celular, computador ou monitor e
              o resultado da impressão.
            </p>
          </RuleCard>
        </Reveal>
      </div>

      <Reveal delay={0.12}>
        <div
          data-testid="art-rules-checklist"
          className="mt-6 rounded-2xl border-2 border-dashed border-[#f58244]/60 bg-[#fff8f3] p-6 sm:p-8"
        >
          <h3 className="text-lg font-bold text-black sm:text-xl">
            Antes de enviar sua arte
          </h3>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {checklist.map((item, i) => (
              <li
                key={item}
                data-testid={`art-rules-checklist-item-${i + 1}`}
                className="flex items-start gap-3 rounded-xl bg-white px-4 py-3 text-sm leading-relaxed text-neutral-700"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f58244] text-white">
                  <Check className="h-3 w-3" strokeWidth={3.4} />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <a
            href={whatsappLink(
              "Olá! Li as regras de envio do ClickDTF e vou enviar minha arte em PNG com fundo transparente, como documento.",
            )}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="art-rules-checklist-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-black px-7 py-4 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-neutral-800"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
            Enviar arte pelo WhatsApp
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
